import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Send, Eye, Newspaper, ListOrdered } from 'lucide-react';
import { Button } from './ui/button';
import QueueEditorDialog from './QueueEditorDialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';
import { adminNewsletterStatus, adminNewsletterRun } from '../services/api';

const fmtHu = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('hu-HU', {
      year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

export const NewsletterTile = ({ adminKey }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmSend, setConfirmSend] = useState(false);
  const [showQueue, setShowQueue] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setStatus(await adminNewsletterStatus(adminKey));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Cikk-sorozat státusz betöltési hiba.');
    } finally {
      setLoading(false);
    }
  }, [adminKey]);

  useEffect(() => { load(); }, [load]);

  const handlePreview = async () => {
    setBusy(true);
    setPreview(null);
    try {
      const res = await adminNewsletterRun(adminKey, { dryRun: true });
      setPreview(res);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Az előnézet nem sikerült.');
    } finally {
      setBusy(false);
    }
  };

  const handleSend = async () => {
    setConfirmSend(false);
    setBusy(true);
    try {
      const res = await adminNewsletterRun(adminKey, {});
      if (res.sent > 0) {
        toast.success(`Kiküldve: ${res.sent} cikk-email${res.failed ? ` (${res.failed} hiba)` : ''}.`);
      } else {
        toast.info('Most senki nem esedékes — a rendszer naponta automatikusan ellenőrzi.');
      }
      setPreview(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'A küldés nem sikerült.');
    } finally {
      setBusy(false);
    }
  };

  const pool = status?.pool || [];

  return (
    <div data-testid="tile-newsletter" className="rounded-2xl border border-gray-800 bg-gradient-to-b from-sky-950/30 to-gray-900/50 p-5">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-lg bg-sky-500/15 flex items-center justify-center">
            <Newspaper className="text-sky-400" size={20} />
          </div>
          <div>
            <h3 className="font-bold text-white">Cikk-sorozat folytatása</h3>
            <p className="text-xs text-gray-500">
              A drip sorozat után hetente 1 cikk, megjelenési sorrendben — az új cikkek automatikusan bekerülnek.
            </p>
          </div>
        </div>
        <div data-testid="tile-newsletter-count" className="text-3xl font-black text-sky-400 leading-none" title="Sorozatot befejezett feliratkozók">
          {loading ? '…' : (status?.recipient_count ?? 0)}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-6"><Loader2 className="animate-spin mx-auto text-sky-400" size={20} /></div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-3 mb-4 text-sm">
            <div className="rounded-lg bg-gray-900/60 border border-gray-800 p-3">
              <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Cikk a sorban</div>
              <div className="text-white font-semibold" data-testid="tile-newsletter-pool">{pool.length}</div>
              <div className="text-xs text-gray-500 mt-0.5 truncate">
                {pool.length ? `Következő: ${pool[0].title}` : 'Publikálj új cikket!'}
              </div>
            </div>
            <div className="rounded-lg bg-gray-900/60 border border-gray-800 p-3">
              <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Most esedékes</div>
              <div className={`font-semibold ${status?.due_now ? 'text-emerald-300' : 'text-gray-400'}`} data-testid="tile-newsletter-due">
                {status?.due_now ?? 0} feliratkozó
              </div>
              {status?.waiting_for_new > 0 && (
                <div className="text-xs text-gray-500 mt-0.5">{status.waiting_for_new} már mindent megkapott</div>
              )}
            </div>
            <div className="rounded-lg bg-gray-900/60 border border-gray-800 p-3">
              <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Utolsó küldés</div>
              <div className="text-white font-semibold text-xs" data-testid="tile-newsletter-last-run">
                {status?.last_send ? fmtHu(status.last_send.sent_at) : 'Még nem volt'}
              </div>
              {status?.last_send && (
                <div className="text-xs text-gray-500 mt-0.5 truncate">{status.last_send.to}</div>
              )}
            </div>
          </div>

          <div data-testid="tile-newsletter-stats" className="rounded-lg bg-gray-900/60 border border-gray-800 p-3 mb-4 text-sm">
            <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1.5">Megnyitási statisztika</div>
            {status?.stats?.webhook_configured === false ? (
              <div className="text-xs text-gray-500">
                Opcionális: a Resend webhook beállítása után itt látod, ki nyitotta meg / kattintott
                (Resend → Webhooks → <span className="font-mono text-gray-400">/api/webhooks/resend</span>).
              </div>
            ) : (
              <div className="flex items-center gap-5">
                <div>
                  <span className="text-white font-bold">{status?.stats?.sent_total ?? 0}</span>
                  <span className="text-xs text-gray-500 ml-1">kiküldve</span>
                </div>
                <div>
                  <span className="text-emerald-300 font-bold">{status?.stats?.opened ?? 0}</span>
                  <span className="text-xs text-gray-500 ml-1">megnyitás</span>
                  {status?.stats?.open_rate_pct != null && (
                    <span className="text-xs text-emerald-400/80 ml-1">({status.stats.open_rate_pct}%)</span>
                  )}
                </div>
                <div>
                  <span className="text-sky-300 font-bold">{status?.stats?.clicked ?? 0}</span>
                  <span className="text-xs text-gray-500 ml-1">kattintás</span>
                </div>
              </div>
            )}
          </div>

          {preview && (
            <div data-testid="tile-newsletter-preview" className="rounded-lg border border-sky-800/40 bg-sky-950/20 p-3 mb-4 text-sm">
              {preview.due > 0 ? (
                <>
                  <div className="text-sky-300 font-semibold mb-1">
                    Előnézet: {preview.due} email menne ki most
                  </div>
                  <ul className="text-xs text-gray-400 space-y-0.5">
                    {preview.plan.slice(0, 8).map((p) => (
                      <li key={p.email}>{p.email} → <span className="text-gray-300">{p.next_article}</span></li>
                    ))}
                    {preview.plan.length > 8 && <li>… és még {preview.plan.length - 8}</li>}
                  </ul>
                </>
              ) : (
                <div className="text-gray-400">
                  Most senki nem esedékes — mindenki a 7 napos ritmusán belül van, vagy már minden cikket megkapott.
                </div>
              )}
            </div>
          )}

          <div className="flex gap-2 flex-wrap">
            <Button
              data-testid="tile-newsletter-queue-btn"
              variant="outline"
              size="sm"
              onClick={() => setShowQueue(true)}
              className="border-gray-700 bg-gray-900/60 text-gray-200 hover:bg-gray-800 hover:text-white"
            >
              <ListOrdered size={14} className="mr-1.5" />
              Sorrend szerkesztése
            </Button>
            <Button
              data-testid="tile-newsletter-preview-btn"
              variant="outline"
              size="sm"
              onClick={handlePreview}
              disabled={busy}
              className="border-gray-700 bg-gray-900/60 text-gray-200 hover:bg-gray-800 hover:text-white"
            >
              {busy ? <Loader2 className="mr-1.5 animate-spin" size={14} /> : <Eye size={14} className="mr-1.5" />}
              Előnézet
            </Button>
            <Button
              data-testid="tile-newsletter-send-btn"
              size="sm"
              onClick={() => setConfirmSend(true)}
              disabled={busy || !status?.due_now}
              className="bg-sky-600 hover:bg-sky-500 text-white font-bold"
            >
              <Send size={14} className="mr-1.5" />
              Esedékesek küldése most
            </Button>
          </div>
          <p className="text-[11px] text-gray-600 mt-2">
            Automatikus: a rendszer naponta ellenőrzi, és 7 naponta küldi a következő cikket minden feliratkozónak.
          </p>
        </>
      )}

      <QueueEditorDialog
        adminKey={adminKey}
        open={showQueue}
        onClose={() => setShowQueue(false)}
        onSaved={load}
      />

      <AlertDialog open={confirmSend} onOpenChange={setConfirmSend}>
        <AlertDialogContent data-testid="tile-newsletter-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Esedékes cikk-emailek kiküldése?</AlertDialogTitle>
            <AlertDialogDescription>
              {status?.due_now ?? 0} feliratkozó kapja meg most a sorozata következő cikkét.
              A 7 napos ritmuson belül lévők kimaradnak.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="tile-newsletter-confirm-cancel">Mégse</AlertDialogCancel>
            <AlertDialogAction
              data-testid="tile-newsletter-confirm-send"
              onClick={handleSend}
              className="bg-sky-600 hover:bg-sky-500"
            >
              <Send size={14} className="mr-1.5" /> Küldés
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default NewsletterTile;
